import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { SkeletonRows } from '../../components/skeleton'
import {
    getCompanyDetail, suspendCompany, reactivateCompany,
    assignPlan, getPlans,
    updateUser, deleteUser, resetUserPassword, deactivateUser, activateUser,
    createCompanyUser,
} from '../../services/platform'
import { onQuerySuccess, onMutateSuccess } from '../../utils/queryResponse'
import refreshPlatform from './refreshPlatform'
import { toast } from '../../utils/toast'
import PlatformConfirm from './platformConfirm'
import CompanyFormModal from './companyFormModal'
import { dateLocale } from '../../i18n/i18n'
import { ASSIGNABLE_ROLES, roleLabel, roleListLabel } from '../../constants/labels'

const AVATAR_COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#F97316']
const getInitials = (name) => { if (!name) return '?'; const parts = name.trim().split(/\s+/); return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase() }
const getColor = (name) => { let h = 0; for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h); return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length] }
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(dateLocale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtDateTime = (d) => d ? new Date(d).toLocaleString(dateLocale(), { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
// The company detail API sends each user's roles in this company as an array
// (`roles: ['admin']`); roleListLabel takes the comma-joined keys.
const userRoles = (u) => (Array.isArray(u.roles) ? u.roles.join(',') : u.role) || ''
// The one role the edit form shows / sets (the panel keeps one role per company).
const firstRole = (u) => userRoles(u).split(',')[0].trim() || 'staff'

const ROLES = ASSIGNABLE_ROLES

// Company.suspendedReason values the API writes → readable wording.
const SUSPEND_REASON_I18N = {
    manual: 'platform.companyDetail.reasonManual',
    trial_expired: 'platform.companyDetail.reasonTrialExpired',
}

const PlatformCompanyDetailPage = () => {
    const { t } = useTranslation()
    const { id } = useParams()
    const qc = useQueryClient()
    const [company, setCompany] = useState(null)
    const [users, setUsers] = useState([])
    const [actionOpen, setActionOpen] = useState(null)
    const [confirm, setConfirm] = useState(null)

    // Modals
    const [showCreateUser, setShowCreateUser] = useState(false)
    const [createForm, setCreateForm] = useState({ fullName: '', email: '', role: 'staff' })
    const [editModal, setEditModal] = useState(null)
    const [editForm, setEditForm] = useState({ fullName: '', email: '', role: '' })
    const [showCompanyForm, setShowCompanyForm] = useState(false)
    const [showPlanModal, setShowPlanModal] = useState(false)
    const [selectedPlan, setSelectedPlan] = useState('')
    const [plans, setPlans] = useState([])

    const { isFetching, data } = useQuery(['platform-company', id], () => getCompanyDetail(id))
    const plansQuery = useQuery('platform-plans', getPlans, { enabled: showPlanModal })

    useEffect(() => { onQuerySuccess(data, (r) => { setCompany(r.company || r); setUsers(r.users || []) }) }, [data])
    useEffect(() => { onQuerySuccess(plansQuery.data, (r) => setPlans(r.plans || r || [])) }, [plansQuery.data])

    const invalidate = () => refreshPlatform(qc)
    // The company owner stays an Administrator (the API refuses to change it).
    const isOwner = (u) => !!u && (company?.owner?.id === u.id)

    const suspendMut = useMutation(() => suspendCompany(id), { onSuccess: onMutateSuccess(invalidate, t('platform.companyActions.suspendedToast')) })
    const reactivateMut = useMutation(() => reactivateCompany(id), { onSuccess: onMutateSuccess(invalidate, t('platform.companyActions.reactivatedToast')) })
    const planMut = useMutation((planId) => assignPlan(id, planId), { onSuccess: onMutateSuccess(() => { invalidate(); setShowPlanModal(false) }, t('platform.companyDetail.planAssignedToast')) })

    const createUserMut = useMutation((p) => createCompanyUser(id, p), { onSuccess: onMutateSuccess(() => { invalidate(); setShowCreateUser(false); setCreateForm({ fullName: '', email: '', role: 'staff' }) }, t('platform.companyDetail.userCreatedToast')) })
    const updateUserMut = useMutation(({ uid, payload }) => updateUser(uid, payload), { onSuccess: onMutateSuccess(() => { invalidate(); setEditModal(null) }, t('platform.userActions.updatedToast')) })
    const deleteUserMut = useMutation(deleteUser, { onSuccess: onMutateSuccess(invalidate, t('platform.userActions.deletedToast')) })
    const resetPwMut = useMutation(resetUserPassword, { onSuccess: onMutateSuccess(() => {}, t('platform.userActions.resetSentToast')) })
    const deactivateUserMut = useMutation(deactivateUser, { onSuccess: onMutateSuccess(invalidate, t('platform.userActions.deactivatedToast')) })
    const activateUserMut = useMutation(activateUser, { onSuccess: onMutateSuccess(invalidate, t('platform.userActions.activatedToast')) })

    if (isFetching && !company) {
        return <div className="sbr" style={{ padding: 28 }}><SkeletonRows rows={8} widths={['30%', '40%', '20%']} /></div>
    }

    if (!company) return <div className="sbr" style={{ padding: 28, color: '#9CA3B0' }}>{t('platform.companyDetail.notFound')}</div>

    const handleCreateUser = (e) => {
        e.preventDefault()
        if (!createForm.fullName || !createForm.email) return toast.error(t('platform.common.nameEmailRequired'))
        createUserMut.mutate(createForm)
    }

    const handleEditUser = (e) => {
        e.preventDefault()
        if (!editForm.fullName) return toast.error(t('platform.common.nameRequired'))
        // The role is per company, so it goes with this company's id — and only
        // when it actually changed.
        const roleChanged = editForm.role && editForm.role !== firstRole(editModal)
        updateUserMut.mutate({ uid: editModal.id, payload: {
            fullName: editForm.fullName,
            ...(editForm.email && { email: editForm.email }),
            ...(roleChanged && { role: editForm.role, companyId: id }),
        } })
    }

    // Suspend / Deactivate / Delete wait for the confirmation modal.
    const askConfirm = (action, id, name) => {
        setConfirm({ action, id, name })
        setActionOpen(null)
    }

    const runConfirmed = ({ action, id }) => {
        if (action === 'suspend') suspendMut.mutate()
        if (action === 'deactivate') deactivateUserMut.mutate(id)
        if (action === 'delete') deleteUserMut.mutate(id)
    }

    const handlePlanSubmit = (e) => {
        e.preventDefault()
        if (!selectedPlan) return toast.error(t('platform.companyDetail.selectPlanError'))
        planMut.mutate(selectedPlan)
    }

    return (
        <div className="sbr">
            {/* Back link */}
            <Link to="/platform/companies" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#697586', textDecoration: 'none', marginBottom: 16 }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                {t('platform.companyDetail.back')}
            </Link>

            {/* Header */}
            <div className="p-detail-header">
                <div className="p-detail-avatar" style={{ background: getColor(company.name) }}>{getInitials(company.name)}</div>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#0B1424' }}>{company.name}</h2>
                        <span className={`p-status-badge ${company.isSuspended ? 'p-status-suspended' : 'p-status-active'}`}>
                            {company.isSuspended ? t('platform.common.suspended') : t('platform.common.active')}
                        </span>
                    </div>
                    <p style={{ fontSize: 13, color: '#697586', margin: '2px 0 0' }}>{company.email || ''}</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-sbr btn-ghost-sbr" onClick={() => setShowCompanyForm(true)} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8 }}>{t('platform.companyActions.edit')}</button>
                    {company.isSuspended ? (
                        <button className="btn-sbr btn-primary-sbr" onClick={() => reactivateMut.mutate()} disabled={reactivateMut.isLoading} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8 }}>{t('platform.companyActions.reactivate')}</button>
                    ) : (
                        <button className="btn-sbr btn-danger-sbr" onClick={() => askConfirm('suspend', company.id, company.name)} disabled={suspendMut.isLoading} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8 }}>{t('platform.companyActions.suspend')}</button>
                    )}
                </div>
            </div>

            {/* Company Info */}
            <div className="card-sbr p-detail-section" style={{ padding: 20 }}>
                <div className="p-detail-section-title">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#697586" strokeWidth="2"><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /></svg>
                    {t('platform.companyDetail.info')}
                </div>
                <div className="p-info-grid">
                    <div className="p-info-item"><div className="label">{t('platform.columns.created')}</div><div className="value">{fmtDate(company.createdAt)}</div></div>
                    <div className="p-info-item"><div className="label">{t('platform.columns.plan')}</div><div className="value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{company.planName || company.plan?.name || t('platform.common.noPlan')} <button className="btn-sbr btn-ghost-sbr" onClick={() => { setSelectedPlan(company.planId || company.plan?.id || ''); setShowPlanModal(true) }} style={{ padding: '2px 8px', fontSize: 11, borderRadius: 6 }}>{t('platform.common.change')}</button></div></div>
                    <div className="p-info-item"><div className="label">{t('platform.companyDetail.trialEnds')}</div><div className="value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{fmtDate(company.trialEndDate)}</div></div>
                    <div className="p-info-item"><div className="label">{t('platform.companyDetail.totalUsers')}</div><div className="value">{users.length}</div></div>
                    {company.phone && <div className="p-info-item"><div className="label">{t('platform.common.phoneLabel')}</div><div className="value">{company.phone}</div></div>}
                    {company.suspendedAt && <div className="p-info-item"><div className="label">{t('platform.companyDetail.suspendedAt')}</div><div className="value">{fmtDateTime(company.suspendedAt)}</div></div>}
                    {company.suspendedReason && <div className="p-info-item"><div className="label">{t('platform.companyDetail.suspendReason')}</div><div className="value">{SUSPEND_REASON_I18N[company.suspendedReason] ? t(SUSPEND_REASON_I18N[company.suspendedReason]) : company.suspendedReason}</div></div>}
                </div>
            </div>

            {/* Users */}
            <div className="card-sbr p-detail-section" style={{ padding: 0 }}>
                <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #EBEEF5' }}>
                    <div className="p-detail-section-title" style={{ margin: 0 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#697586" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
                        {t('platform.companyDetail.usersCount', { count: users.length })}
                    </div>
                    <button className="btn-sbr btn-primary-sbr" onClick={() => setShowCreateUser(true)} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 8 }}>{t('platform.companyDetail.addUser')}</button>
                </div>
                <table className="p-table">
                    <thead>
                        <tr>
                            <th>{t('platform.columns.user')}</th>
                            <th className="p-col-opt">{t('platform.columns.role')}</th>
                            <th>{t('platform.columns.status')}</th>
                            <th className="p-col-opt">{t('platform.columns.lastLogin')}</th>
                            <th className="p-col-opt">{t('platform.columns.joined')}</th>
                            <th style={{ width: 50 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.id}>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: getColor(u.fullName), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{getInitials(u.fullName)}</div>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0B1424' }}>{u.fullName}</div>
                                            <div style={{ fontSize: 11, color: '#9CA3B0' }}>{u.email || u.username}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-col-opt"><span className="p-role-badge">{userRoles(u) ? roleListLabel(userRoles(u)) : '—'}</span></td>
                                <td>
                                    <span className={`p-status-badge ${u.isDeactivated ? 'p-status-suspended' : 'p-status-active'}`}>
                                        {u.isDeactivated ? t('platform.common.deactivated') : t('platform.common.active')}
                                    </span>
                                </td>
                                <td className="p-col-opt" style={{ fontSize: 12, color: '#697586' }}>{u.lastLoginAt ? fmtDateTime(u.lastLoginAt) : t('platform.common.never')}</td>
                                <td className="p-col-opt" style={{ fontSize: 12, color: '#697586' }}>{fmtDate(u.createdAt)}</td>
                                <td>
                                    <div style={{ position: 'relative' }}>
                                        <button style={{ background: 'none', border: '1px solid #EBEEF5', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#697586' }} onClick={() => setActionOpen(actionOpen === u.id ? null : u.id)}>
                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" /></svg>
                                        </button>
                                        {actionOpen === u.id && (
                                            <div className="p-action-dropdown">
                                                <button className="p-action-dropdown-item" onClick={() => { setEditForm({ fullName: u.fullName, email: u.email || '', role: firstRole(u) }); setEditModal(u); setActionOpen(null) }}>
                                                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                    {t('platform.userActions.editDetails')}
                                                </button>
                                                <button className="p-action-dropdown-item" onClick={() => { resetPwMut.mutate(u.id); setActionOpen(null) }}>
                                                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                                                    {t('platform.userActions.resetPassword')}
                                                </button>
                                                <div className="p-action-dropdown-divider" />
                                                {u.isDeactivated ? (
                                                    <button className="p-action-dropdown-item" onClick={() => { activateUserMut.mutate(u.id); setActionOpen(null) }}>
                                                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#16A34A" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                                                        {t('platform.userActions.activate')}
                                                    </button>
                                                ) : (
                                                    <button className="p-action-dropdown-item p-action-dropdown-danger" onClick={() => askConfirm('deactivate', u.id, u.fullName)}>
                                                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                                                        {t('platform.userActions.deactivate')}
                                                    </button>
                                                )}
                                                <button className="p-action-dropdown-item p-action-dropdown-danger" onClick={() => askConfirm('delete', u.id, u.fullName)}>
                                                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                                    {t('platform.userActions.deleteUser')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9CA3B0', padding: 32 }}>{t('platform.companyDetail.noUsers')}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <PlatformConfirm confirm={confirm} onClose={() => setConfirm(null)} onConfirm={runConfirmed} />

            {/* Create User Modal */}
            {showCreateUser && (
                <div className="p-modal-overlay" onClick={() => setShowCreateUser(false)}>
                    <div className="p-modal" onClick={e => e.stopPropagation()}>
                        <h3>{t('platform.companyDetail.addUserTitle', { company: company.name })}</h3>
                        <form onSubmit={handleCreateUser}>
                            <label>{t('platform.common.fullNameLabel')}</label>
                            <input value={createForm.fullName} onChange={e => setCreateForm(f => ({ ...f, fullName: e.target.value }))} placeholder="John Doe" />
                            <label>{t('platform.common.emailRequiredLabel')}</label>
                            <input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} placeholder="john@example.com" />
                            <label>{t('platform.companyDetail.roleLabel')}</label>
                            <select value={createForm.role} onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}>
                                {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                            </select>
                            <div className="p-modal-actions">
                                <button type="button" className="btn-sbr btn-ghost-sbr" onClick={() => setShowCreateUser(false)} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8 }}>{t('platform.common.cancel')}</button>
                                <button type="submit" className="btn-sbr btn-primary-sbr" disabled={createUserMut.isLoading} style={{ padding: '8px 18px', fontSize: 13, borderRadius: 8 }}>{createUserMut.isLoading ? t('platform.common.creating') : t('platform.companyDetail.createInvite')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showCompanyForm && <CompanyFormModal company={company} onClose={() => setShowCompanyForm(false)} />}

            {/* Edit User Modal */}
            {editModal && (
                <div className="p-modal-overlay" onClick={() => setEditModal(null)}>
                    <div className="p-modal" onClick={e => e.stopPropagation()}>
                        <h3>{t('platform.userActions.editTitle')}</h3>
                        <form onSubmit={handleEditUser}>
                            <label>{t('platform.common.fullNameLabel')}</label>
                            <input value={editForm.fullName} onChange={e => setEditForm(f => ({ ...f, fullName: e.target.value }))} />
                            <label>{t('platform.common.emailLabel')}</label>
                            <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                            <label>{t('platform.companyDetail.roleLabel')}</label>
                            <select value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} disabled={isOwner(editModal)}>
                                {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                            </select>
                            {isOwner(editModal) && <p style={{ fontSize: 12, color: '#697586', margin: '6px 0 0' }}>{t('platform.userActions.ownerRoleHint')}</p>}
                            <div className="p-modal-actions">
                                <button type="button" className="btn-sbr btn-ghost-sbr" onClick={() => setEditModal(null)} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8 }}>{t('platform.common.cancel')}</button>
                                <button type="submit" className="btn-sbr btn-primary-sbr" disabled={updateUserMut.isLoading} style={{ padding: '8px 18px', fontSize: 13, borderRadius: 8 }}>{updateUserMut.isLoading ? t('platform.common.saving') : t('platform.common.save')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Assign Plan Modal */}
            {showPlanModal && (
                <div className="p-modal-overlay" onClick={() => setShowPlanModal(false)}>
                    <div className="p-modal" onClick={e => e.stopPropagation()}>
                        <h3>{t('platform.companyDetail.planTitle')}</h3>
                        <form onSubmit={handlePlanSubmit}>
                            <label>{t('platform.companyDetail.selectPlanLabel')}</label>
                            <select value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)}>
                                <option value="">{t('platform.companyDetail.choosePlan')}</option>
                                {plans.map(p => <option key={p.id} value={p.id}>{p.name} {p.price != null ? t('platform.companyDetail.perMonth', { price: '$' + Number(p.price).toFixed(2) }) : ''}</option>)}
                            </select>
                            <div className="p-modal-actions">
                                <button type="button" className="btn-sbr btn-ghost-sbr" onClick={() => setShowPlanModal(false)} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8 }}>{t('platform.common.cancel')}</button>
                                <button type="submit" className="btn-sbr btn-primary-sbr" disabled={planMut.isLoading} style={{ padding: '8px 18px', fontSize: 13, borderRadius: 8 }}>{planMut.isLoading ? t('platform.companyDetail.assigning') : t('platform.companyDetail.assign')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PlatformCompanyDetailPage
